import { type CurrencyCode, Money, isCurrencyCode, minorUnitExponent } from '@salary/domain'

/**
 * Money as it arrives from the API.
 *
 * `currency` is a plain string rather than the domain's union, because that is
 * what crossed the wire. Narrowing it at the type level would be a claim the
 * client cannot make — a row written before a currency was retired still
 * deserializes — so every formatter checks it against the registry and has an
 * answer for the case where it does not match.
 */
export interface MoneyJSON {
  minorUnits: number
  currency: string
}

/**
 * Formatting money in the browser, using the same value object the server does.
 *
 * The exponent comes from the domain's currency registry rather than from
 * `Intl`, so the browser and the API agree about what 15000 means in JPY. A
 * client that divides every amount by a hundred renders Tokyo salaries at a
 * hundredth of their size, and nothing in the API would be wrong.
 */
export function moneyOf(value: MoneyJSON): Money {
  if (!isCurrencyCode(value.currency)) {
    throw new Error(`Unsupported currency from the API: ${value.currency}`)
  }
  return Money.fromMinor(value.minorUnits, value.currency)
}

export function formatMoney(value: MoneyJSON | undefined, options?: { compact?: boolean }): string {
  if (!value) return '—'
  if (!isCurrencyCode(value.currency)) return `${value.minorUnits} ${value.currency}`

  const exponent = minorUnitExponent(value.currency)
  const major = value.minorUnits / 10 ** exponent

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: value.currency,
    // Whole amounts only in tables and headline figures: nobody compares
    // salaries to the penny, and the decimals cost the column its scannability.
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...(options?.compact ? { notation: 'compact', maximumFractionDigits: 1 } : {}),
  }).format(major)
}

/** Exact to the minor unit, for a detail view where the figure is the subject. */
export function formatMoneyExact(value: MoneyJSON | undefined): string {
  if (!value) return '—'
  if (!isCurrencyCode(value.currency)) return `${value.minorUnits} ${value.currency}`

  const exponent = minorUnitExponent(value.currency)
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: value.currency,
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  }).format(value.minorUnits / 10 ** exponent)
}

export function formatPercent(value: number | undefined, digits = 1): string {
  if (value === undefined || Number.isNaN(value)) return '—'
  return `${(value * 100).toFixed(digits)}%`
}

/** Signed, because a change of -3% and a change of 3% are different news. */
export function formatSignedPercent(value: number | undefined, digits = 1): string {
  if (value === undefined || Number.isNaN(value)) return '—'
  return `${value >= 0 ? '+' : ''}${(value * 100).toFixed(digits)}%`
}

/**
 * A compa-ratio, as a percentage of the band midpoint.
 *
 * The underlying figure is a ratio — 0.986 — and that is how a compensation
 * analyst would write it. It is also unreadable to everybody else: a column of
 * numbers between 0.5 and 1.4 with no unit invites the reader to guess. As a
 * percentage it says what it means out loud, because "99% of the midpoint" is
 * a sentence and "0.99" is a puzzle.
 *
 * Whole percent, not two decimals: the extra precision is noise next to a band
 * that was itself set by judgement.
 */
export function formatRatio(value: number | undefined): string {
  return value === undefined ? '—' : `${Math.round(value * 100)}%`
}

export function formatCount(value: number | undefined): string {
  return value === undefined ? '—' : new Intl.NumberFormat('en-GB').format(value)
}

/**
 * A business date, never passed through `new Date()`.
 *
 * `new Date('2026-04-01')` is parsed as midnight UTC and then rendered in the
 * local zone, so west of Greenwich a raise effective on 1 April displays as 31
 * March. These are calendar dates with no time and no zone, and the only safe
 * thing to do is treat them as text.
 */
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

export function formatDate(value: string | undefined): string {
  if (!value) return '—'
  const [year, month, day] = value.split('-')
  const index = Number(month) - 1
  if (!year || !day || !MONTHS[index]) return value
  return `${Number(day)} ${MONTHS[index]} ${year}`
}

/** When the row was written, which really is an instant. */
export function formatTimestamp(value: string | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const CURRENCY_OPTIONS: CurrencyCode[] = [
  'USD',
  'EUR',
  'GBP',
  'INR',
  'SGD',
  'AED',
  'AUD',
  'CAD',
  'PLN',
  'BRL',
  'ZAR',
  'JPY',
]
